import React from 'react';
import type { FormikConfig } from 'formik';
import { Formik } from 'formik';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import { TranslationKeys } from 'app/translations';

import type { EmployeeEmploymentContract, UseQueryRefetch } from 'app/types';

import type { ShowSnakeBarParams } from 'app/hooks';
import {
  useEmployeeCreateEmploymentContractMutation,
  useEmployeeUpdateEmploymentContractMutation,
  useSnakeBar,
} from 'app/hooks';

import { EmployeeEmploymentContractFields } from '../../EmployeeEmploymentContracts.types';
import { EmployeeEmploymentContractFormDialogBody } from './components';
import { useProcessCustomBackendError } from '../../../../../../../../../../hooks/useProcessCustomBackendError';

export type EmployeeEmploymentContractFormValues = {
  startDate: string | null;
  endDate: string | null;
  contractType: string;
  contractId: string | null;
  creationDate: string | null;
  employmentSequence: string | null;
};

type CreateEmployeeEmploymentContractArgs = {
  connectionId: string;
  employeeId: string;
  employmentId: string;
  contract: Partial<EmployeeEmploymentContract>;
};

type UpdateEmployeeEmploymentContractArgs = CreateEmployeeEmploymentContractArgs;

type EmployeeEmploymentContractFormDialogProps = {
  open: boolean;
  closeDialog: () => void;
  connectionId: string;
  employeeId: string;
  employmentId: string | undefined;
  contract: EmployeeEmploymentContract | null;
  refetchGetEmploymentsQuery: UseQueryRefetch;
};

export const EmployeeEmploymentContractFormDialog = ({
  open,
  closeDialog,
  connectionId,
  employeeId,
  employmentId,
  contract,
  refetchGetEmploymentsQuery,
}: EmployeeEmploymentContractFormDialogProps) => {
  const intl = useIntl();
  const { showSnakeBar } = useSnakeBar();
  const processError = useProcessCustomBackendError();

  const { mutate: createEmploymentContract } = useEmployeeCreateEmploymentContractMutation();
  const { mutate: updateEmploymentContract } = useEmployeeUpdateEmploymentContractMutation();

  const isEdit = contract !== null;

  const showSnackBarMessage = React.useCallback(
    ({ method, severity }: Omit<ShowSnakeBarParams, 'message'>) => {
      showSnakeBar({
        severity,
        method,
        message: intl.formatMessage({
          id:
            severity === 'success'
              ? TranslationKeys.global_update_successMessage
              : TranslationKeys.global_update_errorMessage,
        }),
      });
    },
    [intl, showSnakeBar],
  );

  const formikInitialValues = React.useMemo<EmployeeEmploymentContractFormValues>(() => {
    return {
      startDate: contract?.startDate || null,
      endDate: contract?.endDate || null,
      contractType: contract?.contractType || '',
      contractId: contract?.contractId || null,
      creationDate: contract?.creationDate || null,
      employmentSequence: contract?.employmentSequence || null,
    };
  }, [contract]);

  const formikValidationSchema = React.useMemo(() => {
    return Yup.object().shape({
      [EmployeeEmploymentContractFields.StartDate.InputName]: Yup.date().required(),
      [EmployeeEmploymentContractFields.EndDate.InputName]: Yup.date()
        .min(
          Yup.ref(EmployeeEmploymentContractFields.StartDate.InputName),
          intl.formatMessage({ id: TranslationKeys.global_error_endDateBeforeStartDate }),
        )
        .nullable(),
      [EmployeeEmploymentContractFields.ContractType.InputName]: Yup.string().required(),
      [EmployeeEmploymentContractFields.CreationDate.InputName]: Yup.date().nullable(),
    });
  }, [intl]);

  const formikOnSubmit = React.useCallback<FormikConfig<EmployeeEmploymentContractFormValues>['onSubmit']>(
    (values, { setSubmitting, resetForm }) => {
      const onSuccess = (method: string) => () => {
        setSubmitting(false);
        resetForm();
        closeDialog();
        showSnackBarMessage({ method, severity: 'success' });
        refetchGetEmploymentsQuery();
      };

      const onError = (method: string) => (error: any) => {
        setSubmitting(false);
        processError(error, method);
      };

      const mutationData = {
        connectionId,
        employeeId,
        employmentId: employmentId,
        contract: {
          ...(isEdit && contract?.mongoId ? { mongoId: contract.mongoId } : {}),
          contractId: values.contractId || contract?.contractId,
          startDate: values.startDate!,
          endDate: values.endDate || null,
          contractType: values.contractType,
          creationDate: values.creationDate || null,
          employmentSequence: values.employmentSequence || null,
        },
      } as CreateEmployeeEmploymentContractArgs | UpdateEmployeeEmploymentContractArgs;

      if (isEdit) {
        const method = 'updateEmploymentContract';
        updateEmploymentContract(mutationData as UpdateEmployeeEmploymentContractArgs, {
          onSuccess: onSuccess(method),
          onError: onError(method),
        });
      } else {
        const method = 'createEmploymentContract';
        createEmploymentContract(mutationData, {
          onSuccess: onSuccess(method),
          onError: onError(method),
        });
      }
    },
    [
      connectionId,
      employeeId,
      employmentId,
      contract?.contractId,
      contract?.mongoId,
      isEdit,
      updateEmploymentContract,
      closeDialog,
      showSnackBarMessage,
      refetchGetEmploymentsQuery,
      createEmploymentContract,
      processError,
    ],
  );

  return (
    <Formik<EmployeeEmploymentContractFormValues>
      initialValues={formikInitialValues}
      validationSchema={formikValidationSchema}
      onSubmit={formikOnSubmit}
    >
      <EmployeeEmploymentContractFormDialogBody open={open} isEdit={isEdit} closeDialog={closeDialog} />
    </Formik>
  );
};
